import React from "react";
import { CheckCircle2 } from "lucide-react";
import { FieldGroups } from "./FieldGroups";
import { TESTIDS } from "@/lib/testIds";

/**
 * ExtractionReview — the review UX shown after a PDF is extracted.
 * User confirms canonical-line/ship selections (when candidates exist)
 * and edits any of the extracted fields before pressing Create Cruise.
 *
 * ADR-CRUISE-001: this is the identical review UX used by the manual
 * "Add more details" path so both entry paths converge on the same
 * mental model.
 */
export default function ExtractionReview({
  filename,
  fields,
  editField,
  lineCandidates,
  shipCandidates,
  lineId,
  setLineId,
  shipId,
  setShipId,
  openGroups,
  setOpenGroups,
  onSubmit,
  onBack,
  submitting,
}) {
  return (
    <div className="px-6 py-5 space-y-4" data-testid={TESTIDS.createCruiseReview}>
      <div className="flex items-center gap-2 text-[13px] p-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900">
        <CheckCircle2 className="w-4 h-4 shrink-0" />
        <div>
          Extracted from <strong>{filename}</strong>. Review every field and edit anything that looks wrong before creating your cruise.
        </div>
      </div>

      {(lineCandidates?.length > 1) && (
        <CandidatePicker
          label="Which cruise line?"
          candidates={lineCandidates}
          selected={lineId}
          onSelect={setLineId}
          testId={TESTIDS.createCruiseReviewLinePicker}
        />
      )}
      {(shipCandidates?.length > 1) && (
        <CandidatePicker
          label="Which ship?"
          candidates={shipCandidates}
          selected={shipId}
          onSelect={setShipId}
          testId={TESTIDS.createCruiseReviewShipPicker}
        />
      )}

      <FieldGroups
        fields={fields}
        editField={editField}
        openGroups={openGroups}
        setOpenGroups={setOpenGroups}
      />

      <div className="flex justify-between gap-2 pt-3 border-t border-slate-200">
        <button
          onClick={onBack}
          className="px-4 py-2 text-[13px] font-semibold rounded border border-slate-200 text-slate-700 hover:bg-slate-50"
          data-testid={TESTIDS.createCruiseReviewBack}
          type="button"
        >
          ← Back
        </button>
        <button
          onClick={onSubmit}
          disabled={submitting}
          className="px-5 py-2 text-[13px] font-semibold rounded bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-60"
          data-testid={TESTIDS.createCruiseReviewCreate}
          type="button"
        >
          {submitting ? "Creating…" : "Create cruise"}
        </button>
      </div>
    </div>
  );
}

function CandidatePicker({ label, candidates, selected, onSelect, testId }) {
  return (
    <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-3" data-testid={testId}>
      <div className="text-[11px] font-black uppercase tracking-widest text-amber-700 mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {candidates.map((c) => (
          <button
            key={c.id}
            onClick={() => onSelect(c.id)}
            type="button"
            className={`px-3 py-1.5 rounded text-[12.5px] font-semibold border ${
              selected === c.id
                ? "bg-amber-500 text-white border-amber-500"
                : "bg-white text-slate-700 border-slate-200 hover:border-amber-400"
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>
    </div>
  );
}
